/**
 * GoogleDriveProviderCard Component
 *
 * Manages connection to Google Drive for cloud sync storage.
 * Shows connection status, account email, folder link, and connect/disconnect actions.
 */

// 1. External dependencies
import React, { useState } from 'react';
import { Cloud, Link as LinkIcon, Unlink, ExternalLink } from 'lucide-react';

// 2. Internal absolute imports
import {
  Alert,
  Card,
  CardHeader,
  CardBody,
  Button,
  Chip,
  Code
} from '@enfomentis/ui';
import { Brand } from '@enfomentis/shared/config';
import { openOAuthPopup } from '@/utils';

export interface GoogleDriveProviderCardProps {
  /**
   * Callback when Google Drive connect button clicked.
   * Receives pre-opened OAuth popup to avoid browser blocking.
   */
  onConnect: (oauthPopup?: Window | null) => Promise<void>;
  /** Callback when Google Drive disconnected */
  onDisconnect: () => Promise<void>;
  /** Connection status */
  isConnected: boolean;
  /** Connection information from BFF */
  connectionInfo: {
    email: string | null;
    userId: string | null;
    folderId: string | null;
  } | null;
  /** Disable connect button (during connecting/rateLimited states) */
  isConnectDisabled?: boolean;
}

/**
 * Google Drive Provider Card
 *
 * Displays connection status and manages OAuth flow for Google Drive sync.
 * Shows connected account email, folder link, and connect/disconnect actions.
 */
export const GoogleDriveProviderCard: React.FC<GoogleDriveProviderCardProps> = ({
  onConnect,
  onDisconnect,
  isConnected,
  connectionInfo,
  isConnectDisabled = false
}) => {
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [isDisconnecting, setIsDisconnecting] = useState<boolean>(false);
  const [popupBlocked, setPopupBlocked] = useState<boolean>(false);

  const handleConnect = async (): Promise<void> => {
    // Open popup FIRST (synchronous, preserves user gesture)
    const oauthPopup = openOAuthPopup('google-drive');

    // Guard: popup blocked by browser
    if (!oauthPopup) {
      setPopupBlocked(true);
      return;
    }

    // Clear any previous popup blocked state
    setPopupBlocked(false);

    setIsConnecting(true);
    try {
      await onConnect(oauthPopup);
    } catch (error) {
      console.error('Failed to connect to Google Drive:', error);
      // Close popup on error if still open (defensive check)
      if (oauthPopup && !oauthPopup.closed) {
        oauthPopup.close();
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    setIsDisconnecting(true);
    try {
      await onDisconnect();
    } catch (error) {
      console.error('Failed to disconnect from Google Drive:', error);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleOpenFolder = (): void => {
    if (connectionInfo?.folderId) {
      window.open(`https://drive.google.com/drive/folders/${connectionInfo.folderId}`, '_blank');
    }
  };

  // Determine if we're in a "reconnect" state (was connected before but tokens expired)
  const shouldShowReconnect = !isConnected && connectionInfo?.email;

  return (
    <Card className="max-w-sm md:max-w-full">
      <CardHeader className="flex items-center gap-2">
        <Cloud className="w-5 h-5 text-text-secondary" />
        <h3 className="text-lg font-semibold">Google Drive</h3>
        <Chip
          size="sm"
          color={isConnected ? 'success' : 'default'}
          variant="flat"
          className="ml-auto"
        >
          {isConnected ? 'Connected' : 'Not Connected'}
        </Chip>
      </CardHeader>
      <CardBody className="space-y-4 p-4 sm:p-6">
        {/* Account Email Display */}
        <div>
          <p className="text-sm text-text-secondary mb-2">Account</p>
          {isConnected && connectionInfo?.email ? (
            <Code className="flex items-center gap-2 min-w-0 max-w-full">
              {connectionInfo.email}
            </Code>
          ) : shouldShowReconnect && connectionInfo?.email ? (
            <Code className="whitespace-normal">
              {connectionInfo.email} (disconnected)
            </Code>
          ) : (
            <p className="text-sm text-text-tertiary italic">
              No account connected
            </p>
          )}
        </div>

        {/* Folder Link - show if we have a folderId (even when disconnected) */}
        {connectionInfo?.folderId && (
          <div>
            <p className="text-sm text-text-secondary mb-2">
              {isConnected ? 'Sync Folder' : 'Last Sync Folder'}
            </p>
            <Button
              size="md"
              variant="light"
              endContent={<ExternalLink className="w-4 h-4" />}
              onPress={handleOpenFolder}
              className="w-full sm:w-auto whitespace-normal h-auto min-h-[44px]"
            >
              <span>Open "{Brand.driveFolder}" in Drive</span>
            </Button>
          </div>
        )}

        {/* Connection Actions */}
        <div className="flex flex-col sm:flex-row gap-2">
          {!isConnected ? (
            <Button
              color="primary"
              size="lg"
              startContent={<LinkIcon className="w-4 h-4" />}
              onPress={handleConnect}
              isLoading={isConnecting}
              isDisabled={isConnectDisabled}
              className="w-full sm:w-auto"
            >
              {shouldShowReconnect ? 'Reconnect to Google Drive' : 'Connect to Google Drive'}
            </Button>
          ) : (
            <Button
              color="danger"
              variant="light"
              size="lg"
              startContent={<Unlink className="w-4 h-4" />}
              onPress={handleDisconnect}
              isLoading={isDisconnecting}
              className="w-full sm:w-auto"
            >
              Disconnect
            </Button>
          )}
        </div>

        {/* Popup Blocked Warning */}
        {popupBlocked && (
          <Alert
            color="warning"
            variant="flat"
            title="Popup Blocked"
            description="Please allow popups for this site and try again."
          />
        )}

        {/* Help Text */}
        <p className="text-xs text-text-tertiary break-words">
          {!isConnected
            ? 'Connect your Google Drive account to sync your trading journal data securely in the cloud. You can access your data from any device.'
            : 'Your trading journal is syncing to Google Drive. All data is encrypted and stored securely in your personal Drive folder.'}
        </p>
      </CardBody>
    </Card>
  );
};
